import { useState, useEffect } from 'react';
import {
  Users,
  MessageSquare,
  Activity,
  Shield,
  UserCheck,
  DollarSign,
  BarChart3,
  Headphones,
  Clock,
  AlertTriangle,
  Loader2,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { formatDateTime } from '../../utils/dateFormat';
import { formatCompactNumber } from '../../utils/formatNumber';
import axios from 'axios';
import PageTitle from '../../components/PageTitle';

interface StatsData {
  overview: {
    totalUsers: number;
  };
  userStats: Array<{ _id: string; count: number }>;
  dailyStats: Array<{ _id: string; activeUsers: number }>;
  recentUsers: Array<{
    id: string;
    email: string;
    role: string;
    create_at: string;
  }>;
}

interface OrdersStats {
  totalRevenue: number;
}

interface ChatsStats {
  totalChats: number;
  activeChats: number;
  pendingChats: number;
  urgentChats: number;
}

const CHART_COLORS = ['#6366f1', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#ec4899', '#84cc16'];

function DashboardOverview() {
  const [stats, setStats] = useState<StatsData | null>(null);
  const [ordersStats, setOrdersStats] = useState<OrdersStats | null>(null);
  const [chatsStats, setChatsStats] = useState<ChatsStats | null>(null);
  const [loading, setLoading] = useState(true);
  const { t } = useTranslation('dashboard');

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      const [statsRes, ordersRes, chatsRes] = await Promise.all([
        axios.get('/api/admin/stats'),
        axios.get('/api/admin/orders/stats').catch(() => ({ data: null })),
        axios.get('/api/admin/admin-chats/stats').catch(() => ({ data: null })),
      ]);
      setStats(statsRes.data);
      if (ordersRes.data) setOrdersStats(ordersRes.data);
      if (chatsRes.data) setChatsStats(chatsRes.data);
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
      toast.error(t('overview.load_failed'));
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const maxDaily = Math.max(...(stats?.dailyStats || []).map((d) => d.activeUsers), 1);

  return (
    <div className="p-8 space-y-6">
      <PageTitle titleKey="overview.title" namespace="dashboard" descriptionKey="overview.subtitle" />
      {/* Header */}
      <div>
        <h1 className="text-2xl font-semibold text-foreground tracking-tight">{t('overview.title')}</h1>
        <p className="text-sm text-muted-foreground mt-1">{t('overview.subtitle')}</p>
      </div>

      {/* Row 1: KPI Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <KpiCard
            icon={Users}
            label={t('overview.total_users')}
            value={formatCompactNumber(stats.overview.totalUsers)}
            iconBg="bg-blue-50 dark:bg-blue-950/30"
            iconColor="text-blue-600 dark:text-blue-400"
          />
          <KpiCard
            icon={DollarSign}
            label={t('overview.total_revenue')}
            value={ordersStats ? `$${ordersStats.totalRevenue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '$0.00'}
            iconBg="bg-amber-50 dark:bg-amber-950/30"
            iconColor="text-amber-600 dark:text-amber-400"
          />
        </div>
      )}

      {/* Row 2: User Breakdown */}
      <div className="grid grid-cols-1 gap-4">
        {/* User Breakdown */}
        <div className="bg-card border border-border rounded-xl p-5">
          <h3 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
            <Users className="h-4 w-4 text-muted-foreground" />
            {t('overview.user_breakdown')}
          </h3>
          <div className="space-y-3">
            {(stats?.userStats || []).map((entry) => {
              const isAdmin = entry._id === 'admin';
              const total = stats?.overview.totalUsers || 1;
              const pct = ((entry.count / total) * 100).toFixed(1);
              return (
                <div key={entry._id} className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${isAdmin ? 'bg-amber-50 dark:bg-amber-950/30' : 'bg-blue-50 dark:bg-blue-950/30'}`}>
                    {isAdmin
                      ? <Shield className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                      : <UserCheck className="h-4 w-4 text-blue-600 dark:text-blue-400" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm text-foreground">
                        {isAdmin ? t('overview.admin_count') : t('overview.user_count')}
                      </span>
                      <span className="text-xs font-medium text-muted-foreground">
                        {entry.count} ({pct}%)
                      </span>
                    </div>
                    <div className="w-full bg-muted rounded-full h-1.5">
                      <div
                        className={`h-1.5 rounded-full transition-all duration-500 ${isAdmin ? 'bg-amber-500' : 'bg-blue-500'}`}
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  </div>
                </div>
              );
            })}
            {(!stats?.userStats || stats.userStats.length === 0) && (
              <p className="text-sm text-muted-foreground text-center py-6">{t('overview.no_activity_data')}</p>
            )}
          </div>
        </div>
      </div>

      {/* Row 3: Daily Active Users */}
      <div className="bg-card border border-border rounded-xl p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
            <BarChart3 className="h-4 w-4 text-muted-foreground" />
            {t('overview.daily_active')}
          </h3>
          <span className="text-xs text-muted-foreground">{t('overview.last_30_days')}</span>
        </div>
        {stats?.dailyStats && stats.dailyStats.length > 0 ? (
          <div className="flex items-end gap-[3px] h-40 overflow-x-auto">
            {stats.dailyStats.map((day) => {
              const heightPct = (day.activeUsers / maxDaily) * 100;
              const dateLabel = day._id.slice(5); // MM-DD
              return (
                <div key={day._id} className="flex flex-col items-center flex-1 min-w-[14px] group">
                  <div className="relative w-full flex justify-center">
                    <div
                      className="w-full max-w-[18px] rounded-t bg-primary/70 dark:bg-primary/50 group-hover:bg-primary transition-colors"
                      style={{ height: `${Math.max(heightPct, 2)}%` }}
                      title={`${day._id}: ${day.activeUsers}`}
                    />
                    <span className="absolute -top-5 text-[10px] text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                      {day.activeUsers}
                    </span>
                  </div>
                  <span className="text-[9px] text-muted-foreground mt-1 rotate-[-45deg] origin-top-left whitespace-nowrap">
                    {dateLabel}
                  </span>
                </div>
              );
            })}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground text-center py-10">{t('overview.no_activity_data')}</p>
        )}
      </div>

      {/* Row 4: Support Overview + Recent Users */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Support Overview */}
        <div className="bg-card border border-border rounded-xl p-5">
          <h3 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
            <Headphones className="h-4 w-4 text-muted-foreground" />
            {t('overview.support_overview')}
          </h3>
          <div className="grid grid-cols-2 gap-3">
            <SupportStat
              icon={MessageSquare}
              label={t('overview.total_chats')}
              value={chatsStats?.totalChats ?? 0}
              bg="bg-blue-50 dark:bg-blue-950/30"
              color="text-blue-600 dark:text-blue-400"
              valueColor="text-blue-700 dark:text-blue-300"
            />
            <SupportStat
              icon={Activity}
              label={t('overview.active_chats')}
              value={chatsStats?.activeChats ?? 0}
              bg="bg-emerald-50 dark:bg-emerald-950/30"
              color="text-emerald-600 dark:text-emerald-400"
              valueColor="text-emerald-700 dark:text-emerald-300"
            />
            <SupportStat
              icon={Clock}
              label={t('overview.pending_chats')}
              value={chatsStats?.pendingChats ?? 0}
              bg="bg-amber-50 dark:bg-amber-950/30"
              color="text-amber-600 dark:text-amber-400"
              valueColor="text-amber-700 dark:text-amber-300"
            />
            <SupportStat
              icon={AlertTriangle}
              label={t('overview.urgent_chats')}
              value={chatsStats?.urgentChats ?? 0}
              bg="bg-red-50 dark:bg-red-950/30"
              color="text-red-600 dark:text-red-400"
              valueColor="text-red-700 dark:text-red-300"
            />
          </div>
        </div>

        {/* Recent Users */}
        <div className="bg-card border border-border rounded-xl overflow-hidden">
          <div className="px-5 py-4 border-b border-border">
            <h3 className="text-sm font-semibold text-foreground">{t('overview.recent_users')}</h3>
          </div>
          <div className="divide-y divide-border">
            {stats?.recentUsers?.slice(0, 5).map((user) => (
              <div key={user.id} className="flex items-center justify-between px-5 py-3.5 hover:bg-muted/30 transition-colors">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                    <span className="text-xs font-medium text-primary">
                      {(user.email || '?').charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground">{user.email}</p>
                    <p className="text-xs text-muted-foreground">{t('overview.user')}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {user.role === 'admin' ? (
                    <Shield className="h-4 w-4 text-amber-500 dark:text-amber-400" />
                  ) : (
                    <UserCheck className="h-4 w-4 text-emerald-500 dark:text-emerald-400" />
                  )}
                  <span className="text-xs text-muted-foreground">
                    {formatDateTime(user.create_at)}
                  </span>
                </div>
              </div>
            ))}
            {(!stats?.recentUsers || stats.recentUsers.length === 0) && (
              <p className="text-sm text-muted-foreground text-center py-6">{t('overview.no_activity_data')}</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function KpiCard({
  icon: Icon,
  label,
  value,
  iconBg,
  iconColor,
}: {
  icon: any;
  label: string;
  value: string;
  iconBg: string;
  iconColor: string;
}) {
  return (
    <div className="bg-card border border-border rounded-xl p-5">
      <div className="flex items-center gap-4">
        <div className={`p-2.5 rounded-lg ${iconBg}`}>
          <Icon className={`h-5 w-5 ${iconColor}`} />
        </div>
        <div>
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{label}</p>
          <p className="text-xl font-bold text-foreground mt-0.5">{value}</p>
        </div>
      </div>
    </div>
  );
}

function SupportStat({
  icon: Icon,
  label,
  value,
  bg,
  color,
  valueColor,
}: {
  icon: any;
  label: string;
  value: number;
  bg: string;
  color: string;
  valueColor: string;
}) {
  return (
    <div className={`${bg} rounded-lg p-3.5`}>
      <div className="flex items-center gap-1.5 mb-1.5">
        <Icon className={`h-3.5 w-3.5 ${color}`} />
        <span className={`text-xs ${color}`}>{label}</span>
      </div>
      <p className={`text-xl font-bold ${valueColor}`}>{typeof value === 'number' ? formatCompactNumber(value) : value}</p>
    </div>
  );
}

export default DashboardOverview;
